import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { Notification, Page } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

/**
 * The bell's data, in one place (E6, #446, slice 1).
 *
 * No websockets: the count is read again whenever the window regains focus and
 * once a minute while it is open, which is as live as a team of ten needs and
 * needs nothing on the server but the route that already exists.
 *
 * Everything lives under one key so a single `invalidateQueries` after a write
 * refreshes the badge and the panel together — which is what makes marking
 * one read change the count without a page reload.
 */
export const NOTIFICATIONS_KEY = ["notifications"] as const;

/** The badge's own query, so opening the panel can refresh it by name. */
export const UNREAD_COUNT_KEY = [...NOTIFICATIONS_KEY, "unread-count"] as const;

/** About a minute — "an interval of about a minute", not a tight loop. */
export const POLL_MS = 60_000;

const PAGE_SIZE = 20;

/**
 * How many are unread. Stale the moment it arrives (`staleTime: 0`), so the
 * focus refetch is never skipped for being "recent enough": a person tabbing
 * back after a minute expects the number to be right, not roughly right.
 */
export function useUnreadCount() {
  const data = useDataSource();
  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => data.notifications.unreadCount(),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: POLL_MS,
    // Every screen mounts a bell, and the interval already keeps it fresh —
    // a request per navigation would add nothing.
    refetchOnMount: false,
  });
}

/** The panel's rows, unread first, a page at a time. Only fetched while open. */
export function useNotificationList(enabled: boolean) {
  const data = useDataSource();
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, "list"],
    queryFn: ({ pageParam }): Promise<Page<Notification>> =>
      data.notifications.list({ limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: enabled ? POLL_MS : false,
  });
}

export function useMarkRead() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => data.notifications.markRead(id),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

export function useMarkAllRead() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => data.notifications.markAllRead(),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}
